import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { formatCheckTimestamp, stripPayerIdSuffix } from '../utils/formatters'
import { IconSparkle } from './Icons'

/**
 * Shared slideout header. Every eligibility slideout (Medical / Stedi, VSP,
 * EyeMed) renders the same top region through these components so they are
 * visually identical and can't drift apart.
 *
 * The captions and field set are HARDCODED here on purpose — callers pass
 * values only, never labels. A value a given source lacks renders as an em
 * dash, never as a different layout or a missing field. Layout classes
 * (`med-summary-*`) live in Dashboard.css and predate this extraction.
 */

export const DASH = '—'

/** ISO 8601 date → "MM/DD/YYYY". Composed here so every slideout's sub-line is
 *  formatted identically. */
function formatDate(iso: string): string {
  if (!iso || !iso.includes('-')) return iso || ''
  const [y, m, d] = iso.split('-')
  return `${m}/${d}/${y}`
}

// ── Header bar (icon + title + subtitle + optional actions + close) ──────────

export function SlideoutHeaderBar({ icon, title, subtitle, actions, onClose }: {
  icon: ReactNode
  title: string
  subtitle: string
  /** Right-aligned controls between the subtitle and the close button
   *  (e.g. Medical's JSON / Edit / Cancel buttons). */
  actions?: ReactNode
  onClose: () => void
}) {
  return (
    <div className="med-summary-header">
      <div className="med-summary-header__icon">{icon}</div>
      <div className="med-summary-header__content">
        <h2 className="med-summary-header__title">{title}</h2>
        <p className="med-summary-header__subtitle">{subtitle}</p>
      </div>
      {actions}
      <button className="slideout-panel__close" onClick={onClose} aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

// ── Verification record (payer-verified date stamp + chart permalink) ────────

/**
 * The load-bearing payer-dispute record: when the check was verified with the
 * payer, plus a one-click permalink to file in the patient chart. Renders
 * nothing when neither a timestamp nor a check id is available (e.g. a
 * freshly-run check with no filed record yet).
 */
export function SlideoutVerificationRecord({ checkedAt, timezone, checkId }: {
  /** ISO 8601 timestamp the check was verified with the payer. */
  checkedAt?: string
  /** Clinic IANA timezone so the stamp renders in the clinic's local time. */
  timezone?: string
  /** Stored check id — enables the `?check=<id>` chart permalink. */
  checkId?: string
}) {
  if (!checkedAt && !checkId) return null

  async function handleCopyChartLink() {
    if (!checkId) return
    const url = `${window.location.origin}/?check=${checkId}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied — paste it into the patient chart')
    } catch {
      toast.error('Could not copy the link.')
    }
  }

  return (
    <div className="med-summary-record">
      {checkedAt ? (
        <div className="med-summary-record__stamp" title="Date this eligibility was verified with the payer">
          <span className="med-summary-record__stamp-label">Payer verified</span>
          <span className="med-summary-record__stamp-value">{formatCheckTimestamp(checkedAt, timezone)}</span>
        </div>
      ) : <span />}
      {checkId && (
        <button type="button" className="med-summary-record__copy" onClick={handleCopyChartLink} title="Copy a permanent link to this check to save in the patient's chart">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          Copy link
        </button>
      )}
    </div>
  )
}

// ── Payer name value (with the "Suggested by VitalRev AI" badge) ─────────────

/**
 * Renders a payer name for the Payer Details grid, appending the provenance
 * suffix + sparkle badge when the check was opened from an appointment whose
 * raw payer shorthand (`resolvedFrom`) differs from the resolved directory
 * name. Compared case-insensitively so casing-only drift doesn't trigger a
 * false positive.
 */
export function SlideoutPayerValue({ name, resolvedFrom }: {
  name: string
  /** Raw payer name the check ran against (Revolution / CSV shorthand). */
  resolvedFrom?: string | null
}) {
  const isResolved = !!resolvedFrom
    && !!name
    && resolvedFrom.trim().toLowerCase() !== name.trim().toLowerCase()
  return (
    <>
      {stripPayerIdSuffix(name)}
      {isResolved && (
        <span className="med-payer-from">
          {` · from "${resolvedFrom}"`}
          <span
            className="med-ai-badge"
            data-ai-tooltip={`Original payer name was "${resolvedFrom}"\nVitalRev AI matched it to this payer`}
            aria-label={`Original payer name was "${resolvedFrom}" - VitalRev AI matched it to this payer`}
          >
            <IconSparkle />
          </span>
        </span>
      )}
    </>
  )
}

// ── Read-only summary (patient / provider split + payer details grid) ────────
//
// Both sub-components hardcode their captions and field set so Medical, VSP and
// EyeMed render an identical header. Callers pass values only; a missing value
// becomes an em dash — the layout never changes.

export interface SlideoutSummaryData {
  /** Patient / subscriber display name. */
  patientName?: ReactNode
  /** Renders the "Dependent" tag next to the name. */
  isDependent?: boolean
  /** Patient date of birth (ISO 8601). Rendered as the "DOB: mm/dd/yyyy"
   *  sub-line — composed here so every slideout reads identically. */
  dob?: string
  /** Patient's relation to the plan, already display-formatted ("Subscriber" /
   *  "Spouse" / "Child" — a 271 value). Medical only: vision sources don't
   *  return it, so vision slideouts omit it and their sub-line stays DOB-only. */
  relationship?: string
  /** Provider display name. */
  providerName?: ReactNode
  /** Provider NPI — always rendered as "NPI: <value or em dash>". */
  providerNpi?: ReactNode
  /** Failure slideouts set this so the caption over the patient name reads
   *  "CHECKED FOR" instead of "COVERAGE VERIFIED FOR" — a failed check must
   *  never claim a verification that didn't happen. */
  unverified?: boolean
}

/** Patient + Provider split. Both columns and the DOB sub-line always render
 *  the same way, so the two slideouts are identical regardless of source. */
export function SlideoutSummary({ patientName, isDependent, dob, relationship, providerName, providerNpi, unverified }: SlideoutSummaryData) {
  return (
    <div className="med-summary-split">
      <div className="med-summary-split__col">
        <span className="med-label">{unverified ? 'CHECKED FOR' : 'COVERAGE VERIFIED FOR'}</span>
        <div className="med-value-large">
          {patientName || DASH}
          {isDependent && (
            <span className="member-identity-banner__dep-tag">Dependent</span>
          )}
        </div>
        {(relationship || dob) && (
          <div className="med-meta">
            {relationship}
            {relationship && dob && ' · '}
            {dob && `DOB: ${formatDate(dob)}`}
          </div>
        )}
      </div>
      <div className="med-summary-split__divider" />
      <div className="med-summary-split__col">
        <span className="med-label">PROVIDER</span>
        <div className="med-value-large">{providerName || DASH}</div>
        <div className="med-meta">NPI: {providerNpi || DASH}</div>
      </div>
    </div>
  )
}

export interface SlideoutPayerData {
  /** Payer name node — use `SlideoutPayerValue` to get the AI-match badge. */
  payer?: ReactNode
  plan?: ReactNode
  memberId?: ReactNode
  group?: ReactNode
  /** Optional coverage level; only rendered when present (Medical). */
  coverage?: ReactNode
}

function PayerItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="med-summary-grid__item">
      <span className="med-label">{label}</span>
      <div className="med-value">{value || DASH}</div>
    </div>
  )
}

/** Payer Details grid. Payer / Plan / Member ID / Group always render (em dash
 *  when absent); Coverage renders only when the source supplies it.
 *
 *  KNOWN / ACCEPTED (do not re-flag in review): vision (VSP/EyeMed) and failed
 *  checks carry no Plan or Group, so those rows render an em dash there. That's
 *  intentional — the grid keeps one identical layout across Medical / VSP /
 *  EyeMed / failure slideouts so they can't drift apart; the empty rows are a
 *  deliberate trade for that uniformity, not missing data. */
export function SlideoutPayerGrid({ payer, plan, memberId, group, coverage }: SlideoutPayerData) {
  return (
    <div className="med-summary-section">
      <div className="med-label-title">PAYER DETAILS</div>
      <div className="med-summary-grid">
        <PayerItem label="Payer" value={payer} />
        <PayerItem label="Plan" value={plan} />
        <PayerItem label="Member ID" value={memberId} />
        <PayerItem label="Group" value={group} />
        {coverage != null && coverage !== '' && <PayerItem label="Coverage" value={coverage} />}
      </div>
    </div>
  )
}
